/**
 * Functional Status Trending Chart
 * 
 * Visualizes patient functional status over time with:
 * - KPS/PPS score trend lines
 * - Hospice eligibility threshold line
 * - Decline rate indicators
 * - Predictive trajectory (optional)
 */

import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Area, ComposedChart
} from "recharts";
import {
  TrendingDown, TrendingUp, Minus, Activity, Calendar,
  AlertTriangle, CheckCircle, RefreshCw, Loader2, Info
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate demo data for visualization
const generateDemoData = (patientId, days = 90) => {
  const data = [];
  const baseKPS = 85;
  const basePPS = 80;
  let currentKPS = baseKPS;
  let currentPPS = basePPS;
  
  for (let i = days; i >= 0; i -= 7) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    
    // Simulate gradual decline with some variation
    currentKPS = Math.max(20, currentKPS - Math.random() * 5 + 1);
    currentPPS = Math.max(20, currentPPS - Math.random() * 4 + 0.5);
    
    data.push({
      date: date.toISOString().split('T')[0],
      dateLabel: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      kps: Math.round(currentKPS),
      pps: Math.round(currentPPS),
      hospiceThreshold: 70
    });
  }
  
  return data;
};

// Calculate trend statistics
const calculateTrend = (data, field) => {
  if (!data || data.length < 2) return { direction: 'stable', change: 0, rate: 0 };
  
  const recent = data.slice(-4); // Last 4 data points
  const older = data.slice(0, 4); // First 4 data points
  
  const recentAvg = recent.reduce((sum, d) => sum + (d[field] || 0), 0) / recent.length;
  const olderAvg = older.reduce((sum, d) => sum + (d[field] || 0), 0) / older.length;
  
  const change = recentAvg - olderAvg;
  const rate = (change / olderAvg) * 100;
  
  return {
    direction: change < -5 ? 'declining' : change > 5 ? 'improving' : 'stable',
    change: Math.round(change),
    rate: Math.round(rate),
    current: Math.round(recentAvg),
    previous: Math.round(olderAvg)
  };
};

// Predict future trajectory
const predictTrajectory = (data, field, daysAhead = 30) => {
  if (!data || data.length < 4) return [];
  
  // Simple linear regression
  const n = data.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  
  data.forEach((d, i) => {
    sumX += i;
    sumY += d[field] || 0;
    sumXY += i * (d[field] || 0);
    sumX2 += i * i;
  });
  
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  const predictions = [];
  const lastDate = new Date(data[data.length - 1].date);
  
  for (let i = 1; i <= Math.ceil(daysAhead / 7); i++) {
    const futureDate = new Date(lastDate);
    futureDate.setDate(futureDate.getDate() + (i * 7));
    
    const predictedValue = Math.max(0, Math.min(100, intercept + slope * (n + i - 1)));
    
    predictions.push({
      date: futureDate.toISOString().split('T')[0],
      dateLabel: futureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      [`${field}Predicted`]: Math.round(predictedValue),
      hospiceThreshold: 70,
      isPrediction: true
    });
  }
  
  return predictions;
};

// Custom tooltip
const CustomTooltip = ({ active, payload, label, isDark }) => {
  if (!active || !payload || !payload.length) return null;
  
  return (
    <div className={`p-3 rounded-lg shadow-lg border ${
      isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"
    }`}>
      <p className={`text-sm font-medium mb-2 ${isDark ? "text-white" : "text-gray-900"}`}>
        {label}
      </p>
      {payload.map((entry, idx) => (
        <div key={idx} className="flex items-center gap-2 text-sm">
          <div 
            className="w-3 h-3 rounded-full" 
            style={{ backgroundColor: entry.color }}
          />
          <span className={isDark ? "text-slate-300" : "text-gray-600"}>
            {entry.name}: 
          </span>
          <span className={`font-medium ${
            entry.value <= 70 ? "text-amber-400" : isDark ? "text-white" : "text-gray-900"
          }`}>
            {entry.value}
            {entry.name.includes('Predicted') && ' (predicted)'}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function FunctionalStatusTrend({
  patientId,
  patientName,
  isDark = true,
  showPredictions = true,
  compact = false
}) {
  const token = localStorage.getItem("token");
  
  // States
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("90");
  const [activeMetric, setActiveMetric] = useState("both");
  const [showPrediction, setShowPrediction] = useState(showPredictions);

  // Fetch assessment history
  useEffect(() => {
    const fetchHistory = async () => {
      if (!patientId) {
        // Use demo data if no patient
        setData(generateDemoData("demo", parseInt(timeRange)));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await fetch(
          `${API_URL}/api/lcd-criteria/patient/${patientId}/evaluations?limit=50`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (res.ok) {
          const result = await res.json();
          
          if (result.evaluations && result.evaluations.length > 0) {
            // Transform evaluations to chart data
            const chartData = result.evaluations
              .map(evalItem => ({
                date: evalItem.created_at || evalItem.evaluated_at,
                dateLabel: new Date(evalItem.created_at || evalItem.evaluated_at)
                  .toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                kps: evalItem.functional_assessment?.kps_score || evalItem.evaluation_result?.kps_score,
                pps: evalItem.functional_assessment?.pps_score || evalItem.evaluation_result?.pps_score,
                hospiceThreshold: 70
              }))
              .filter(d => d.kps || d.pps)
              .sort((a, b) => new Date(a.date) - new Date(b.date));
            
            setData(chartData.length > 0 ? chartData : generateDemoData(patientId, parseInt(timeRange)));
          } else {
            setData(generateDemoData(patientId, parseInt(timeRange)));
          }
        } else {
          setData(generateDemoData(patientId, parseInt(timeRange)));
        }
      } catch (error) {
        console.error("Failed to fetch assessment history:", error);
        setData(generateDemoData(patientId, parseInt(timeRange)));
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [patientId, token, timeRange]);

  // Calculate trends
  const kpsTrend = useMemo(() => calculateTrend(data, 'kps'), [data]);
  const ppsTrend = useMemo(() => calculateTrend(data, 'pps'), [data]);

  // Add predictions to data
  const chartData = useMemo(() => {
    if (!showPrediction || data.length < 4) return data;
    
    const kpsPredictions = predictTrajectory(data, 'kps');
    const ppsPredictions = predictTrajectory(data, 'pps');
    
    // Merge predictions
    const merged = [...data];
    kpsPredictions.forEach((pred, i) => {
      merged.push({
        ...pred,
        ppsPredicted: ppsPredictions[i]?.ppsPredicted
      });
    });
    
    return merged;
  }, [data, showPrediction]);

  // Get trend icon
  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'declining':
        return <TrendingDown className="w-4 h-4 text-red-400" />;
      case 'improving':
        return <TrendingUp className="w-4 h-4 text-emerald-400" />;
      default:
        return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  // Days until threshold crossed (prediction)
  const daysUntilThreshold = useMemo(() => {
    if (!showPrediction || data.length < 4) return null;
    
    const predictions = chartData.filter(d => d.isPrediction);
    const crossingPoint = predictions.find(d => 
      (d.kpsPredicted && d.kpsPredicted <= 70) || 
      (d.ppsPredicted && d.ppsPredicted <= 70)
    );
    
    if (!crossingPoint) return null;
    
    const today = new Date();
    const crossDate = new Date(crossingPoint.date);
    const days = Math.ceil((crossDate - today) / (1000 * 60 * 60 * 24));
    
    return days > 0 ? days : null;
  }, [chartData, showPrediction, data.length]);

  if (compact) {
    return (
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"} data-testid="functional-trend-compact">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-400" />
              <span className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                Functional Trend
              </span>
            </div>
            <Badge className={
              kpsTrend.direction === 'declining' 
                ? "bg-red-500/20 text-red-400" 
                : kpsTrend.direction === 'improving'
                  ? "bg-emerald-500/20 text-emerald-400"
                  : "bg-slate-500/20 text-slate-400"
            }>
              {getTrendIcon(kpsTrend.direction)}
              <span className="ml-1">{kpsTrend.change > 0 ? '+' : ''}{kpsTrend.change}</span>
            </Badge>
          </div>
          
          <div className="h-24">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.slice(-8)}>
                <Line 
                  type="monotone" 
                  dataKey="kps" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={false}
                />
                <ReferenceLine y={70} stroke="#f59e0b" strokeDasharray="3 3" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          <div className="flex items-center justify-between mt-2 text-xs">
            <span className={isDark ? "text-slate-400" : "text-gray-500"}>
              KPS: {kpsTrend.current}
            </span>
            {daysUntilThreshold && (
              <span className="text-amber-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                ~{daysUntilThreshold}d to threshold
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"} data-testid="functional-trend-chart">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-400" />
              Functional Status Trend
              {patientName && (
                <span className={`text-sm font-normal ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  - {patientName}
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Track functional decline over time with predictive trajectory
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className={`w-28 ${isDark ? "bg-slate-800 border-slate-700" : ""}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="180">6 months</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowPrediction(!showPrediction)}
              className={showPrediction ? "text-violet-400" : ""}
            >
              {showPrediction ? "Hide" : "Show"} Prediction
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* Trend Summary Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Current KPS</span>
                  {getTrendIcon(kpsTrend.direction)}
                </div>
                <p className={`text-2xl font-bold ${
                  kpsTrend.current <= 70 ? "text-amber-400" : "text-blue-400"
                }`}>
                  {kpsTrend.current}
                </p>
                <p className={`text-xs ${kpsTrend.change < 0 ? "text-red-400" : "text-emerald-400"}`}>
                  {kpsTrend.change > 0 ? '+' : ''}{kpsTrend.change} from baseline
                </p>
              </div>
              
              <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Current PPS</span>
                  {getTrendIcon(ppsTrend.direction)}
                </div>
                <p className={`text-2xl font-bold ${
                  ppsTrend.current <= 70 ? "text-amber-400" : "text-purple-400"
                }`}>
                  {ppsTrend.current}%
                </p>
                <p className={`text-xs ${ppsTrend.change < 0 ? "text-red-400" : "text-emerald-400"}`}>
                  {ppsTrend.change > 0 ? '+' : ''}{ppsTrend.change}% from baseline
                </p>
              </div>
              
              <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Decline Rate</span>
                  <TrendingDown className="w-4 h-4 text-red-400" />
                </div>
                <p className="text-2xl font-bold text-red-400">
                  {Math.abs(kpsTrend.rate)}%
                </p>
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  Over {timeRange} days
                </p>
              </div>
              
              <div className={`p-3 rounded-lg ${
                daysUntilThreshold 
                  ? "bg-amber-500/10 border border-amber-500/30" 
                  : isDark ? "bg-slate-800" : "bg-gray-100"
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                    Threshold Prediction
                  </span>
                  {daysUntilThreshold ? (
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                  ) : (
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  )}
                </div>
                <p className={`text-2xl font-bold ${
                  daysUntilThreshold ? "text-amber-400" : "text-emerald-400"
                }`}>
                  {daysUntilThreshold ? `~${daysUntilThreshold}d` : "N/A"}
                </p>
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  {daysUntilThreshold ? "Until hospice threshold" : "Above threshold"}
                </p>
              </div>
            </div>

            {/* Chart */}
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke={isDark ? "#334155" : "#e5e7eb"} 
                  />
                  <XAxis 
                    dataKey="dateLabel" 
                    tick={{ fill: isDark ? "#94a3b8" : "#6b7280", fontSize: 12 }}
                    axisLine={{ stroke: isDark ? "#475569" : "#d1d5db" }}
                  />
                  <YAxis 
                    domain={[0, 100]}
                    tick={{ fill: isDark ? "#94a3b8" : "#6b7280", fontSize: 12 }}
                    axisLine={{ stroke: isDark ? "#475569" : "#d1d5db" }}
                  />
                  <Tooltip content={<CustomTooltip isDark={isDark} />} />
                  <Legend />
                  
                  {/* Hospice threshold reference line */}
                  <ReferenceLine 
                    y={70} 
                    stroke="#f59e0b" 
                    strokeDasharray="5 5"
                    label={{ 
                      value: "Hospice Threshold (70)", 
                      fill: "#f59e0b", 
                      fontSize: 11,
                      position: "insideTopRight"
                    }}
                  />
                  
                  {/* KPS line */}
                  {(activeMetric === "both" || activeMetric === "kps") && (
                    <Line 
                      type="monotone" 
                      dataKey="kps" 
                      name="KPS Score"
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ fill: "#3b82f6", r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  )}
                  
                  {/* PPS line */}
                  {(activeMetric === "both" || activeMetric === "pps") && (
                    <Line 
                      type="monotone" 
                      dataKey="pps" 
                      name="PPS Score"
                      stroke="#a855f7" 
                      strokeWidth={2}
                      dot={{ fill: "#a855f7", r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  )}
                  
                  {/* Predicted KPS */}
                  {showPrediction && (activeMetric === "both" || activeMetric === "kps") && (
                    <Line 
                      type="monotone" 
                      dataKey="kpsPredicted" 
                      name="KPS Predicted"
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                    />
                  )}
                  
                  {/* Predicted PPS */}
                  {showPrediction && (activeMetric === "both" || activeMetric === "pps") && (
                    <Line 
                      type="monotone" 
                      dataKey="ppsPredicted" 
                      name="PPS Predicted"
                      stroke="#a855f7" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Legend Info */}
            <div className={`mt-4 p-3 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-400 mt-0.5" />
                <div className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                  <p className="mb-1">
                    <strong>KPS (Karnofsky)</strong> and <strong>PPS (Palliative)</strong> scores below 70 
                    typically indicate hospice eligibility. The dashed lines show predicted trajectory 
                    based on historical decline rate.
                  </p>
                  <p>
                    Prediction is for planning purposes only. Clinical judgment should always guide 
                    eligibility decisions.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
